import { useState, useEffect, useCallback } from 'react'
import {
  Page,
  Layout,
  Card,
  Tabs,
  DataTable,
  Badge,
  Button,
  Filters,
  TextField,
  Select,
  EmptyState,
  Spinner,
  Modal,
  TextContainer,
  FormLayout,
  LegacyStack as Stack,
  Icon,
  Banner,
} from '@shopify/polaris'
import {
  StarFilledMinor,
  RefreshMinor,
  ExportMinor,
  SettingsMinor,
} from '@shopify/polaris-icons'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

interface Review {
  id: string
  platform: string
  rating: number
  title?: string
  content?: string
  productTitle?: string
  status: string
  createdAt: string
  customerName?: string
}

interface ReviewRequest {
  id: string
  platform: string
  requestMethod: string
  status: string
  sentAt?: string
  opened: boolean
  clicked: boolean
  responded: boolean
  reminderCount: number
}

interface ReviewAnalytics {
  totalRequests: number
  totalReviews: number
  conversionRate: number
  averageRating: number
  ratingDistribution: Array<{ rating: number; count: number }>
  recentReviews: Review[]
}

export default function Reviews() {
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)

  // Reviews state
  const [reviews, setReviews] = useState<Review[]>([])
  const [reviewsTotal, setReviewsTotal] = useState(0)

  // Review requests state
  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([])

  // Analytics state
  const [analytics, setAnalytics] = useState<ReviewAnalytics | null>(null)

  // Filters
  const [platformFilter, setPlatformFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [ratingFilter, setRatingFilter] = useState<string | undefined>()
  const [searchQuery, setSearchQuery] = useState('')

  // Modals
  const [settingsModalActive, setSettingsModalActive] = useState(false)

  const tabs = [
    { id: 'all-reviews', content: 'All Reviews', panelID: 'all-reviews-panel' },
    { id: 'requests', content: 'Review Requests', panelID: 'requests-panel' },
    { id: 'analytics', content: 'Analytics', panelID: 'analytics-panel' },
  ]

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true)
      const params: any = {}

      if (platformFilter) params.platform = platformFilter
      if (statusFilter) params.status = statusFilter
      if (ratingFilter) {
        const [min, max] = ratingFilter.split('-')
        if (min) params.minRating = min
        if (max) params.maxRating = max
      }

      const response = await axios.get(`${import.meta.env.VITE_API_URL}/reviews`, {
        params,
        headers: { 'x-store-id': localStorage.getItem('storeId') },
      })

      setReviews(response.data.reviews || [])
      setReviewsTotal(response.data.total || 0)
    } catch (error) {
      console.error('Failed to fetch reviews:', error)
    } finally {
      setLoading(false)
    }
  }, [platformFilter, statusFilter, ratingFilter])

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/reviews/analytics`, {
        headers: { 'x-store-id': localStorage.getItem('storeId') },
      })

      setAnalytics(response.data)
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
    }
  }, [])

  // Sync reviews from platform
  const syncReviews = async (platform: string) => {
    try {
      setSyncing(true)
      await axios.post(
        `${import.meta.env.VITE_API_URL}/reviews/sync`,
        { platform },
        { headers: { 'x-store-id': localStorage.getItem('storeId') } }
      )

      await fetchReviews()
      await fetchAnalytics()
    } catch (error) {
      console.error('Failed to sync reviews:', error)
    } finally {
      setSyncing(false)
    }
  }

  useEffect(() => {
    fetchReviews()
    fetchAnalytics()
  }, [fetchReviews, fetchAnalytics])

  // Render rating stars
  const renderRating = (rating: number) => {
    return (
      <Stack spacing="extraTight">
        {Array.from({ length: 5 }).map((_, i) => (
          <Icon
            key={i}
            source={StarFilledMinor}
            color={i < rating ? 'warning' : 'subdued'}
          />
        ))}
      </Stack>
    )
  }

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, any> = {
      PENDING: { status: 'info', children: 'Pending' },
      PUBLISHED: { status: 'success', children: 'Published' },
      REJECTED: { status: 'critical', children: 'Rejected' },
      ARCHIVED: { status: 'default', children: 'Archived' },
      SENT: { status: 'success', children: 'Sent' },
      OPENED: { status: 'info', children: 'Opened' },
      CLICKED: { status: 'attention', children: 'Clicked' },
      RESPONDED: { status: 'success', children: 'Responded' },
      FAILED: { status: 'critical', children: 'Failed' },
      SCHEDULED: { status: 'info', children: 'Scheduled' },
    }

    return <Badge {...(statusMap[status] || { children: status })} />
  }

  // Reviews table
  const reviewsRows = reviews.map((review) => [
    renderRating(review.rating),
    review.productTitle || 'Order Review',
    review.title || '-',
    review.content?.substring(0, 100) + (review.content && review.content.length > 100 ? '...' : '') || '-',
    <Badge>{review.platform}</Badge>,
    getStatusBadge(review.status),
    new Date(review.createdAt).toLocaleDateString(),
  ])

  // All Reviews Panel
  const allReviewsPanel = (
    <Layout>
      <Layout.Section>
        <Card>
          <Card.Section>
            <Stack distribution="equalSpacing">
              <Stack.Item fill>
                <Filters
                  queryValue={searchQuery}
                  queryPlaceholder="Search reviews..."
                  filters={[
                    {
                      key: 'platform',
                      label: 'Platform',
                      filter: (
                        <Select
                          label="Platform"
                          options={[
                            { label: 'All', value: '' },
                            { label: 'Judge.me', value: 'JUDGE_ME' },
                            { label: 'Yotpo', value: 'YOTPO' },
                            { label: 'Stamped.io', value: 'STAMPED' },
                            { label: 'Internal', value: 'INTERNAL' },
                          ]}
                          value={platformFilter || ''}
                          onChange={setPlatformFilter}
                        />
                      ),
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      filter: (
                        <Select
                          label="Status"
                          options={[
                            { label: 'All', value: '' },
                            { label: 'Pending', value: 'PENDING' },
                            { label: 'Published', value: 'PUBLISHED' },
                            { label: 'Rejected', value: 'REJECTED' },
                          ]}
                          value={statusFilter || ''}
                          onChange={setStatusFilter}
                        />
                      ),
                    },
                    {
                      key: 'rating',
                      label: 'Rating',
                      filter: (
                        <Select
                          label="Rating"
                          options={[
                            { label: 'All', value: '' },
                            { label: '5 stars', value: '5-5' },
                            { label: '4+ stars', value: '4-5' },
                            { label: '3+ stars', value: '3-5' },
                            { label: '1-2 stars', value: '1-2' },
                          ]}
                          value={ratingFilter || ''}
                          onChange={setRatingFilter}
                        />
                      ),
                    },
                  ]}
                  onQueryChange={setSearchQuery}
                  onQueryClear={() => setSearchQuery('')}
                  onClearAll={() => {
                    setPlatformFilter(undefined)
                    setStatusFilter(undefined)
                    setRatingFilter(undefined)
                    setSearchQuery('')
                  }}
                />
              </Stack.Item>
              <Button
                icon={RefreshMinor}
                onClick={() => fetchReviews()}
                loading={syncing}
              >
                Refresh
              </Button>
            </Stack>
          </Card.Section>

          {loading ? (
            <Card.Section>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner />
              </div>
            </Card.Section>
          ) : reviews.length === 0 ? (
            <Card.Section>
              <EmptyState
                heading="No reviews yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Start collecting reviews from your customers to build trust and social proof.</p>
              </EmptyState>
            </Card.Section>
          ) : (
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Rating', 'Product', 'Title', 'Content', 'Platform', 'Status', 'Date']}
              rows={reviewsRows}
            />
          )}
        </Card>
      </Layout.Section>
    </Layout>
  )

  // Analytics Panel
  const analyticsPanel = analytics ? (
    <Layout>
      <Layout.Section>
        <Layout>
          <Layout.Section oneThird>
            <Card>
              <Card.Section>
                <TextContainer>
                  <p style={{ color: '#6d7175', fontSize: '14px' }}>Total Requests</p>
                  <p style={{ fontSize: '32px', fontWeight: 600 }}>{analytics.totalRequests}</p>
                </TextContainer>
              </Card.Section>
            </Card>
          </Layout.Section>

          <Layout.Section oneThird>
            <Card>
              <Card.Section>
                <TextContainer>
                  <p style={{ color: '#6d7175', fontSize: '14px' }}>Total Reviews</p>
                  <p style={{ fontSize: '32px', fontWeight: 600 }}>{analytics.totalReviews}</p>
                </TextContainer>
              </Card.Section>
            </Card>
          </Layout.Section>

          <Layout.Section oneThird>
            <Card>
              <Card.Section>
                <TextContainer>
                  <p style={{ color: '#6d7175', fontSize: '14px' }}>Conversion Rate</p>
                  <p style={{ fontSize: '32px', fontWeight: 600 }}>
                    {analytics.conversionRate.toFixed(1)}%
                  </p>
                </TextContainer>
              </Card.Section>
            </Card>
          </Layout.Section>
        </Layout>
      </Layout.Section>

      <Layout.Section>
        <Card title="Average Rating">
          <Card.Section>
            <Stack alignment="center" spacing="loose">
              <div style={{ fontSize: '48px', fontWeight: 600 }}>
                {analytics.averageRating.toFixed(1)}
              </div>
              {renderRating(Math.round(analytics.averageRating))}
            </Stack>
          </Card.Section>
        </Card>
      </Layout.Section>

      <Layout.Section>
        <Card title="Rating Distribution">
          <Card.Section>
            {analytics.ratingDistribution.map((item) => (
              <div key={item.rating} style={{ marginBottom: '1rem' }}>
                <Stack distribution="fillEvenly">
                  <Stack.Item>
                    {item.rating} {item.rating === 1 ? 'star' : 'stars'}
                  </Stack.Item>
                  <Stack.Item fill>
                    <div
                      style={{
                        height: '24px',
                        backgroundColor: '#e3e3e3',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${(item.count / analytics.totalReviews) * 100}%`,
                          backgroundColor: '#5c6ac4',
                        }}
                      />
                    </div>
                  </Stack.Item>
                  <Stack.Item>{item.count}</Stack.Item>
                </Stack>
              </div>
            ))}
          </Card.Section>
        </Card>
      </Layout.Section>

      <Layout.Section>
        <Card title="Recent Reviews">
          <Card.Section>
            {analytics.recentReviews.length === 0 ? (
              <EmptyState
                heading="No reviews yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Recent reviews will appear here.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text']}
                headings={['Rating', 'Product', 'Review', 'Date']}
                rows={analytics.recentReviews.map((review) => [
                  renderRating(review.rating),
                  review.productTitle || 'Order Review',
                  review.content?.substring(0, 100) + '...' || '-',
                  new Date(review.createdAt).toLocaleDateString(),
                ])}
              />
            )}
          </Card.Section>
        </Card>
      </Layout.Section>
    </Layout>
  ) : (
    <Card>
      <Card.Section>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Spinner />
        </div>
      </Card.Section>
    </Card>
  )

  return (
    <Page
      title="Review Management"
      primaryAction={{
        content: 'Settings',
        icon: SettingsMinor,
        onAction: () => setSettingsModalActive(true),
      }}
      secondaryActions={[
        {
          content: 'Sync Reviews',
          icon: RefreshMinor,
          onAction: () => syncReviews('JUDGE_ME'),
          loading: syncing,
        },
        {
          content: 'Export',
          icon: ExportMinor,
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          <Banner status="info">
            <p>
              Automatically collect reviews from customers via WhatsApp. Connect with Judge.me, Yotpo, or
              Stamped.io, or use our internal review system.
            </p>
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
              <Card.Section>
                {selectedTab === 0 && allReviewsPanel}
                {selectedTab === 1 && (
                  <EmptyState
                    heading="Review Requests"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>Track review requests and their status here.</p>
                  </EmptyState>
                )}
                {selectedTab === 2 && analyticsPanel}
              </Card.Section>
            </Tabs>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Settings Modal */}
      <Modal
        open={settingsModalActive}
        onClose={() => setSettingsModalActive(false)}
        title="Review Settings"
        primaryAction={{
          content: 'Save',
          onAction: () => setSettingsModalActive(false),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setSettingsModalActive(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Primary Platform"
              options={[
                { label: 'Internal', value: 'INTERNAL' },
                { label: 'Judge.me', value: 'JUDGE_ME' },
                { label: 'Yotpo', value: 'YOTPO' },
                { label: 'Stamped.io', value: 'STAMPED' },
              ]}
              value="INTERNAL"
              onChange={() => {}}
            />

            <TextField
              label="Request Delay (days after delivery)"
              type="number"
              value="3"
              onChange={() => {}}
              helpText="How many days after delivery to send review request"
            />

            <TextField
              label="Max Reminders"
              type="number"
              value="2"
              onChange={() => {}}
              helpText="Maximum number of reminder messages to send"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  )
}
